/**
 * `useDatabaseList` — the shared, stale-while-revalidate read of a
 * connection's database list.
 *
 * Every database picker used to call `getDatabases()` itself on open and paint
 * "Loading databases…" over a list the Object Explorer had already fetched
 * seconds earlier. The listing is a real server query (30s bound, plus an
 * MSSQL driver-pool warm-up), so that wait was real. This hook reads the ONE
 * cache slot the Explorer already fills on connect —
 * `databaseObjectsQueryKey.databases(connectionId)`, written by
 * `useDatabaseQueries.fetchDatabases` — so the second and later opens paint
 * from cache.
 *
 * Contract (all three parts are load-bearing):
 *
 * 1. `isInitialLoading` is true ONLY when there is nothing cached to paint. A
 *    picker holding cached rows must never render a loading row — that is the
 *    whole point of the change. `isRefreshing` covers the other case: a
 *    background revalidation over rows that are already on screen. Pickers may
 *    show a subtle indicator for it but MUST keep the list interactive.
 *
 * 2. A revalidation ALWAYS runs when the picker opens. `staleTime: 0` +
 *    `refetchOnMount: 'always'`, with `enabled` flipped by the caller's open
 *    state, means react-query issues exactly one real listing per open (its
 *    own dedupe collapses two mounted pickers on the same connection into one
 *    fetch). Not optional: it is what makes a `CREATE DATABASE` the user just
 *    ran in the Query Editor appear, and what keeps the E2E listing seams
 *    (`__queueListingResults`, `__holdListing`'s `afterCall`,
 *    `listingCallCounts`) consuming entries. The gate is `subscribed` as well as
 *    `enabled`, so a closed picker leaves the cache entry exactly as it found
 *    it — see the comment on the `useQuery` call.
 *
 * 3. `databases` is authoritative ONLY on `state === 'ok'`. On
 *    `'error'` / `'empty'` it is `[]` and the surface renders the fallback
 *    presentation (`FallbackSelectContent` / `DatabaseListingFallbackBanner`)
 *    instead. That is the Slice 5b contract: the engine's hardcoded fallback
 *    list is never painted as if it were a listing. Deriving it here means a
 *    picker cannot get it wrong by reading `result.databases` directly.
 *
 * 4. The SHARED slot only ever holds a listing taken during a LIVE connect. A
 *    consumer whose connection is not connected (a restored Data Import draft, a
 *    picker still mounted after a disconnect) still lists — it just lists under
 *    the unshared key, with `gcTime: 0`. See
 *    `databaseListSharing.canShareDatabaseListing`, which owns that rule, and
 *    `connectionListingLifecycle`, which drops the shared slot at every connect
 *    boundary; together they are what makes `gcTime: Infinity` safe.
 *
 * The shared key deliberately carries NO engine segment. The engine is a
 * property *of* the connection — resolved here exactly the way
 * `useDatabaseFetch.fetchDatabasesForConnection` resolves it (saved profile
 * first, then the live `activeConnections` entry, then `'mssql'`) — so
 * `(connectionId, engine)` can never disagree with `(connectionId)`. A key
 * that carried the engine would just split identical rows into a second slot,
 * which is precisely what the Security Manager's `['databases', cid, engine]`
 * key used to do.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import type { DatabaseEngineId } from '@/core/engines';
import { useConnectionStore } from '../../connections/store/connectionStore';
import {
  canShareDatabaseListing,
  databaseListQueryKey,
  isConnectionLive,
} from '../services/databaseListSharing';
import {
  getDatabases,
  type DatabasesListingError,
  type DatabasesListingState,
  type GetDatabasesResult,
} from '../services/databasesService';

type UseDatabaseListOptions = {
  connectionId: string | undefined;
  /**
   * Engine fallback, used ONLY when the connection is in neither store list
   * (a caller that already knows the engine — Table Explorer, Security
   * Manager — passes it so a not-yet-loaded store cannot downgrade to
   * `'mssql'`).
   */
  engine?: DatabaseEngineId | string;
  /**
   * Gate. Pickers pass their open state, so exactly one revalidation runs per
   * open; surfaces that load on mount (Security Manager toolbar, login form)
   * leave it at the default `true`. Always false without a `connectionId`.
   */
  enabled?: boolean;
};

export type UseDatabaseListResult = {
  /** The whole listing result, for callers that need `accessibleDatabases` etc. */
  result: GetDatabasesResult | undefined;
  /** The list to render — `[]` unless `state === 'ok'` (Slice 5b). */
  databases: string[];
  state: DatabasesListingState | undefined;
  error: DatabasesListingError | undefined;
  /** True only while fetching with NOTHING cached to paint. */
  isInitialLoading: boolean;
  /** True while revalidating over rows that are already on screen. */
  isRefreshing: boolean;
  refetch: () => Promise<unknown>;
};

export function useDatabaseList({
  connectionId,
  engine: engineFallback,
  enabled = true,
}: UseDatabaseListOptions): UseDatabaseListResult {
  // Same resolution order as `useDatabaseFetch` — saved profile, then the live
  // entry (browser-mode SQLite exists only there), then the legacy default.
  const connections = useConnectionStore((s) => s.connections);
  const activeConnections = useConnectionStore((s) => s.activeConnections);
  const engine = connectionId
    ? connections.find((c) => c.id === connectionId)?.engine ||
      activeConnections.find((c) => c.id === connectionId)?.connection.engine ||
      engineFallback ||
      'mssql'
    : engineFallback || 'mssql';

  // THE SHARED SLOT HOLDS ONLY A LIVE CONNECT'S LISTING. `enabled` is the
  // caller's open state, which says nothing about whether the connection is up —
  // and a mounted consumer whose connection disconnects keeps rendering. Without
  // the liveness half of the sharing rule it re-created the entry the lifecycle
  // eviction had just removed (usually with a `NO_POOL` failure in it), and since
  // `connectionListingLifecycle` cannot fire a transition for a connect that never
  // happened, `gcTime: Infinity` then retained that failure for the session and
  // served it to the next connect. See `databaseListSharing.canShareDatabaseListing`
  // for the rule and for what each not-connected surface does instead.
  const connectionIsLive = isConnectionLive(activeConnections, connectionId);
  const canShare = canShareDatabaseListing(connectionId, { connectionIsLive });
  const queryKey = useMemo(
    () => databaseListQueryKey(connectionId, { connectionIsLive }),
    [connectionId, connectionIsLive],
  );

  const isOn = enabled && !!connectionId;

  // RETENTION. The shared slot is retained for the whole session: closed pickers
  // are not observers (`subscribed: false`) and the Explorer writes it through
  // `fetchQuery` with no observer at all, so react-query's default 5-minute
  // `gcTime` would evict a listing the Explorer tree is still DISPLAYING — and
  // the next picker opened after six idle minutes would paint "Loading
  // databases…" again, which is the entire bug this hook exists to fix.
  // (`updateGcTime` only ever grows, so this and `fetchDatabases`'
  // `gcTime: Infinity` agree by construction.)
  //
  // Unbounded is safe because the entry has an explicit end of life rather than a
  // timer, and BOTH ends are covered:
  //   • it can only be created during a live connect (the sharing rule above), and
  //   • `connectionListingLifecycle` cancels and removes it at every connect
  //     boundary — the connect ending (disconnect / delete / reconnect-in-place)
  //     AND a connect becoming live, so a fresh connect always starts empty —
  //     mounted or not; `updateConnection` removes it when an edit changes what
  //     `getDatabases` would return.
  //
  // An UNSHARED listing (a connection-dialog probe, an `ignoreConnectionLimit`
  // list, a not-live connection) must not outlive its observer — it is per-click
  // data no picker and no producer reads. A query that is not ON, though, has
  // nothing to retain OR to drop: it never fetches, so `gcTime: 0` would only make
  // react-query build and immediately collect a dataless entry on every render.
  // That covers both "no `connectionId`" (every "Not connected" query tab) and a
  // CLOSED picker on a not-live connection — `QuickActionsBar` mounts
  // `DatabaseSelectorDialog` with `open={false}` and the current connection id, so
  // that second case is the ordinary start page. Keyed off `isOn`, the same gate
  // `enabled` and `subscribed` use.
  const gcTime = canShare ? Infinity : isOn ? 0 : undefined;

  const query = useQuery<GetDatabasesResult>({
    queryKey,
    // No options object is ever passed: `ignoreConnectionLimit` and the
    // `onPhase` reporter belong to the surfaces this hook deliberately does
    // not serve (see Docs/ObjectExplorer.md → recorded gaps).
    queryFn: () => getDatabases(connectionId as string, engine),
    enabled: isOn,
    // A GATED picker must register no observer at all, not a disabled one.
    // react-query calls a query with observers that are ALL disabled
    // `isDisabled()`, and BOTH `refetchQueries` and `invalidateQueries` skip
    // disabled queries — so a single mounted-but-closed picker would make the
    // Object Explorer's own listing un-refetchable through those APIs. That is
    // not hypothetical: `QuickActionsBar` (the start page) mounts
    // `DatabaseSelectorDialog` and `TableDesignerDialog` unconditionally with
    // `open={false}`, which silently froze the shared entry — caught by
    // `src/test/e2e/features/objectExplorer/pinning.spec.ts` (a pinned database
    // stopped going stale after its database disappeared from the catalog).
    // `subscribed: false` keeps the observer out of the query's observer list
    // while still reading whatever the cache holds, so the slot behaves exactly
    // as it did before any picker mounted.
    subscribed: isOn,
    // `0` + `'always'` = "revalidate on every open". The Explorer's own
    // producer keeps its own window (30s for an `'ok'` listing, `0` otherwise);
    // staleness is per-observer, so the two do not fight.
    staleTime: 0,
    refetchOnMount: 'always',
    gcTime,
  });

  const result = query.data;
  const state: DatabasesListingState | undefined =
    result?.state ?? (query.isError ? 'error' : undefined);
  const error: DatabasesListingError | undefined =
    result?.error ??
    (query.isError
      ? { message: query.error instanceof Error ? query.error.message : String(query.error) }
      : undefined);

  const databases = useMemo(
    () => (result?.state === 'ok' ? result.databases : []),
    [result],
  );

  return {
    result,
    databases,
    state,
    error,
    isInitialLoading: query.isFetching && result === undefined,
    isRefreshing: query.isFetching && result !== undefined,
    refetch: query.refetch,
  };
}
